import { ProductStock } from "@hermes/api-model-product";
import { getStockTypeAndDisplayOnly } from "@hermes/fragments/product-utils";
import {
    AnalyticsEvent,
    GTMEcommerceData,
    NO,
    NODATA,
    PRODUCT_IN_STOCK,
    PRODUCT_OUT_OF_STOCK,
    YES,
} from "@hermes/utils/analytics";
import { BRAND } from "@hermes/utils-generic/constants";

import { hasStockRetailOnly } from "../helpers/grid-product.helper";
import { GridProduct } from "../model/grid.model";

/**
 * This class is used for product-click event in grid page
 */
export class GridResultProductClickEvent
    implements AnalyticsEvent<GTMEcommerceData>
{
    /**
     * Event name expected for every productClick event
     */
    public name: string = "productClick";
    /**
     * Event's data from component to format correctly product-click event
     */
    public eventData!: {
        product: GridProduct;
        index: number;
        fromQuickbuy: boolean;
        pageType: string;
        clienttype?: string;
        localeCode: string;
        currencyCode: string;
    };

    constructor(eventData: {
        product: GridProduct;
        index: number;
        fromQuickbuy: boolean;
        pageType: string;
        clienttype?: string;
        localeCode: string;
        currencyCode: string;
    }) {
        this.eventData = eventData;
    }

    /**
     * Overload formatForGTM of inherited class
     * Return formatter for ecommerce key
     */
    public formatForGTM(): GTMEcommerceData {
        return {
            event: this.name,
            ecommerce: {
                currencyCode: this.eventData.currencyCode,
                click: {
                    products: [this.getProductData(this.eventData.product)],
                    actionField: {
                        list: this.eventData.pageType,
                    },
                },
            },
        };
    }

    /**
     * Formatter for product
     *
     * @param product product clicked
     */
    private getProductData = (product: GridProduct) => ({
        name: product.title,
        id: product.sku,
        price: product.price,
        brand: BRAND,
        category: `${product.departmentCode}/${product.familyCode}`,
        variant: product.avgColor || NODATA,
        // eslint-disable-next-line unicorn/explicit-length-check
        dimension5: product.size || NODATA,
        dimension6: product.departmentCode,
        dimension7: product.familyCode,
        dimension8: product.productCode,
        dimension9: product.divisionCode,
        dimension10: NODATA,
        dimension37: product.stock.ecom
            ? PRODUCT_IN_STOCK
            : PRODUCT_OUT_OF_STOCK,
        dimension52: product.isEngravable || product.personalize ? YES : NO,
        dimension53: getStockTypeAndDisplayOnly(
            product.stock as ProductStock,
            product.displayMode,
        ),
        quickbuy_modal: this.eventData.fromQuickbuy ? YES : NO,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        in_store_only: String(hasStockRetailOnly(product)),
        position: this.eventData.index + 1,
        list: this.eventData.pageType,
        vto_available:
            product.isVto || product.vtoType !== undefined ? YES : NO,
        quickbuy_available:
            product.quickBuyProperties?.eligible || this.eventData.fromQuickbuy
                ? YES
                : NO,
        fragrance_finder: "no",
    });
}
